import { Component, OnInit } from "@angular/core";
import { debounceTime } from "rxjs/operators";
import { Subject } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MasterTenantsService } from "../../../services/master-tenants.service";
import { ActivatedRoute } from "@angular/router";
import { EditDetailComponent } from "../edit-detail/edit-detail.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "../../../../../environments/environment";
import { FeaturedListingService } from "../../../../core/services/featured-listing.service";
import { I18nService } from "../../../services";
import * as moment from "moment";
import { PropertyService } from "../../../services/property.services";
import { Clipboard } from "@angular/cdk/clipboard";
import { SiilaTranslationService } from "../../../../core/services/siila-translation.service";
import { Title } from "@angular/platform-browser";
import { LangSelectorPipe } from "../../../pipes/lang-selector.pipe";
import { Router } from "@angular/router";

@Component({
  selector: "app-tenants-detail",
  templateUrl: "./tenants-detail.component.html",
  styleUrls: ["./tenants-detail.component.scss"]
})
export class TenantsDetailComponent implements OnInit {
  data;
  list = [];
  property;
  isRetail = false;
  lastQuarterDate;
  id;
  isAdmin = false;
  isDetail = false;
  showContactMap = new Map<number, boolean>();
  showOwnerMap = new Map<number, boolean>();
  spotVacancy;
  currentCode: string;
  browserLang: string;

  constructor(
    private masterTenantsService: MasterTenantsService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private featureListingService: FeaturedListingService,
    private i18: I18nService,
    private snackBar: MatSnackBar,
    private propertyService: PropertyService,
    private clipboard: Clipboard,
    private siilaTranslationService: SiilaTranslationService,
    private titleService: Title,
    private langSelectorPipe: LangSelectorPipe,
    private router: Router
  ) {
    this.currentCode = environment.platformId;
    this.browserLang = this.siilaTranslationService.getCurrentLanguage();
  }

  ngOnInit(): void {
    let user = JSON.parse(localStorage.getItem("user"));
    this.isAdmin = user?.roles?.includes("ROLE_ADMIN");
    if (
      !user?.roles?.includes("ROLE_USER") &&
      !user?.roles?.includes("ROLE_ADMIN") &&
      !user?.roles?.includes("ROLE_PROPERTIES")
    ) {
      this.snackBar.open("You don't have access to this page", "Close", {
        duration: 3000, // Duration in milliseconds
        horizontalPosition: "center",
        verticalPosition: "top",
        panelClass: ["custom-snackbar"]
      });
      this.router.navigateByUrl("/");
    }

    this.route.params.subscribe(params => {
      if (params.id) {
        this.id = params.id;
        this.getTenantsDetail(params);
        this.getBuilding();
        this.getProperty();
      }
    });
  }

  getProperty() {
    this.propertyService.getPropertyId(this.id).then((resp: any) => {
      this.property = resp;
      this.masterTenantsService
        .getLastDayQuarter(this.property?.buildingType?.id)
        .then((date: any) => {
          this.lastQuarterDate = date;
        });
    });
  }

  getTenantsDetail(params?) {
    this.masterTenantsService.getTenantsDetail(this.id).then((resp: any) => {
      this.data = resp;
      const title = this.langSelectorPipe.transform(this.data.title);
      this.titleService.setTitle(
        this.i18.get("detail-tenant.tabName") + ": " + title
      );
      this.propertyService.getListingsSpotVacancy(this.id).then(vacancy => {
        this.spotVacancy = vacancy;
      });
      if (params?.new) {
        this.editTenantDetail(null, true);
      }
    });
  }

  getBuilding() {
    this.masterTenantsService.getBuilding(this.id).then((resp: any) => {
      this.list = resp;
    });
  }

  editTenantDetail(tenant?, isCreate = false) {
    const dialogRef = this.dialog.open(EditDetailComponent, {
      width: "800px",
      panelClass: "owner-list",
      data: {
        data: tenant,
        buildingType: this.data?.buildingType?.id,
        isAdmin: this.isAdmin,
        isCreate: isCreate,
        includedInQuarterlyStats: this.data.includedInQuarterlyStats,
        industrialStatus: this.data.industrialStatus,
        listTenants: this.list
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.deleted && tenant?.id) {
        this.list = this.list.filter(t => t.id !== tenant.id);

        this.masterTenantsService
          .updateTenantDetail(this.id, this.list)
          .then(() => {
            this.showMessage("master-tenant.deleteMessage");
            this.getBuilding();
          });

        return;
      }
      if (result.updatedTenant) {
        const [year, month, day] = this.getLastDayOfLastQuarter();
        result.updatedTenant.dateEntered = new Date(
          moment.utc({ year, month: month - 1, day }).format("MM/DD/YYYY")
        );

        // use this.lastDayOfQuarter instead of calling getLastDayOfLastQuarter again
        result.updatedTenant.dateEntered = this.lastQuarterDate.lastDayOfQuarter;
        result.updatedTenant.publishedDate = new Date();

        if (result?.idIndustry || tenant?.marketSegment) {
          result.updatedTenant.marketSegment = result.idIndustry
            ? { id: result.idIndustry }
            : { id: tenant.marketSegment.id };
        }

        const tenantOption = this.validateTenantName(result.updatedTenant);

        if (isCreate) {
          result.updatedTenant.building = { id: this.id };
          result.updatedTenant.areaUOM = { id: 9101 };

          this.list.push(result.updatedTenant);
        } else {
          this.list = this.list.map(tenant =>
            tenant.id === result.updatedTenant.id
              ? { ...tenant, ...result.updatedTenant }
              : tenant
          );
        }

        this.masterTenantsService
          .updateTenantDetail(this.id, this.list)
          .then((response: any) => {
            this.showMessage(
              isCreate
                ? "master-tenant.createMessage"
                : "master-tenant.updateMessage"
            );
            this.getBuilding();
          });
      }
    });
  }

  validateTenantName(updatedTenant: any): void {
    let name = updatedTenant?.tenantName;

    let option = {
      Vacant: {
        id: 1,
        name: `{"pt-br":"Vago","en":"Vacant","es":"Disponible"}`
      },
      Vago: { id: 1, name: `{"pt-br":"Vago","en":"Vacant","es":"Disponible"}` },
      Unknown: {
        id: 2,
        name: `{"pt-br":"Desconhecido","en":"Unknown","es":"Desconocido"}`
      },
      Desconhecido: {
        id: 2,
        name: `{"pt-br":"Desconhecido","en":"Unknown","es":"Desconocido"}`
      },
      Desconocido: {
        id: 2,
        name: `{"pt-br":"Desconhecido","en":"Unknown","es":"Desconocido"}`
      },
      Disponible: {
        id: 1,
        name: `{"pt-br":"Desconhecido","en":"Unknown","es":"Disponible"}`
      }
    }[name];

    if (option) {
      updatedTenant.tenantName = null;
      updatedTenant.tenantNameOption = {
        ...option,
        warehouse: null,
        warehouseAlias: null
      };
    }
  }

  validateContactInformation(tenant) {
    return (
      (!tenant.corporation &&
        (tenant?.tenantContactName ||
          tenant?.tenantContactPhone ||
          tenant?.tenantContactEmail)) ||
      (tenant.corporation &&
        (tenant?.corporation?.contactName ||
          tenant?.corporation?.contactPhone ||
          tenant?.corporation?.contactUrl ||
          tenant?.corporation?.contactEmail))
    );
  }

  validateContactCorporation(tenant) {
    return (
      tenant?.corporation?.contactName ||
      tenant?.corporation?.contactPhone ||
      tenant?.corporation?.contactEmail ||
      tenant?.corporation?.contactUrl
    );
  }

  validateOwnersInformation(tenant) {
    return (
      tenant?.owners ||
      tenant?.ownersGroup ||
      tenant?.ownersContactName ||
      tenant?.ownersContactPhone ||
      tenant?.ownersContactEmail
    );
  }

  toggleDetailsContact(id: number): void {
    const current = this.showContactMap.get(id);
    this.showContactMap.set(id, !current);
  }

  toggleDetailsOwner(id: number): void {
    const current = this.showOwnerMap.get(id);
    this.showOwnerMap.set(id, !current);
  }

  downloadTenants() {
    this.masterTenantsService
      .downloadTenantDetail(this.id)
      .then((resp: any) => {
        if (resp.success) {
          const fileUrl1 = `${environment.apiURL}/transactionPrints/files/${resp.fileName}`;
          this.downloadFile(fileUrl1, resp.fileName);
        }
      });
  }

  downloadFile(url: string, fileName: string) {
    if (!url || !fileName) {
      return;
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  getLastDayOfLastQuarter() {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
    const year =
      currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const lastMonth = (lastQuarter + 1) * 3 - 1;
    const lastDay = new Date(year, lastMonth + 1, 0);

    const day = String(lastDay.getDate()).padStart(2, "0");
    const month = String(lastDay.getMonth() + 1).padStart(2, "0");

    return [lastDay.getFullYear(), Number(month), Number(day)];
  }

  onDetailToggle(value: boolean): void {
    this.isDetail = value;

    if (!value) {
      this.showContactMap.clear();
      this.showOwnerMap.clear();
    }
  }

  async openVacancy() {
    let lang = this.i18.getCurrentLanguage();

    this.featureListingService.latamCountry = null;

    let res: any = await this.featureListingService
      .getSpotBuildingDetailURLFormat(this.spotVacancy[0].id, lang)
      .toPromise();

    if (res) {
      let detailsURL = `${res}`;
      detailsURL += "?scrollIntoVac=true";
      window.open(detailsURL, "_blank");
    }
  }

  showMessage(message) {
    this.snackBar.open(this.i18.get(message), "Close", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["custom-snackbar"]
    });
  }

  validateTenantOption(option) {
    return (
      option == "Desconhecido" || option == "Unknown" || option == "Desconocido"
    );
  }

  copyTenantInfo(tenant: any): void {
    const info = tenant.corporation
      ? [
          tenant?.corporation?.contactName,
          tenant?.corporation?.contactPhone,
          tenant?.corporation?.contactEmail,
          tenant?.corporation?.contactUrl
        ]
      : [
          tenant?.tenantContactName,
          tenant?.tenantContactPhone,
          tenant?.tenantContactEmail
        ];

    const formattedInfo = info.filter(Boolean).join("\n");

    if (info) {
      this.clipboard.copy(formattedInfo);
    }
  }

  editValidation() {
    if (!this.isAdmin || !this.data?.industrialStatus?.id) {
      return false;
    }

    const { id } = this.data.industrialStatus;
    const included = this.data.includedInQuarterlyStats;

    const validStatuses = included
      ? [2, 2001, 3, 3001]
      : [1, 1001, 2, 2001, 3, 3001];

    return validStatuses.includes(id);
  }

  getTenantName(tenant) {
    if (tenant.corporation) {
      return tenant.corporation.name;
    }
    return tenant?.tenantName
      ? tenant.tenantName
      : tenant?.tenantNameOption
      ? this.langSelectorPipe.transform(tenant.tenantNameOption.name)
      : "";
  }

  getIndustryName(data, tenant) {
    // Rewrite this: data?.buildingType?.id == 3001
    //            ? (tenant?.industryRetail?.name | langSelector)
    //            : tenant?.marketSegment?.name
    //            ? (tenant?.marketSegment?.name | langSelector)
    //            : tenant?.tenantName == null &&
    //              validateTenantOption(
    //                tenant?.tenantNameOption?.name | langSelector
    //              )
    //            ? "N/A"
    //            : ""
    if (data?.buildingType?.id == 3001 && tenant?.corporation) {
      return tenant.corporation.industryRetail
        ? this.langSelectorPipe.transform(
            tenant.corporation.industryRetail.name
          )
        : "";
    }

    if (data?.buildingType?.id != 3001 && tenant?.corporation) {
      return tenant.corporation.industry
        ? this.langSelectorPipe.transform(tenant.corporation.industry.name)
        : "";
    }

    if (data?.buildingType?.id == 3001) {
      return tenant?.industryRetail?.name
        ? this.langSelectorPipe.transform(tenant.industryRetail.name)
        : tenant?.tenantName == null &&
          this.validateTenantOption(
            this.langSelectorPipe.transform(tenant?.tenantNameOption?.name)
          )
        ? "N/A"
        : "";
    } else {
      return tenant?.marketSegment?.name
        ? this.langSelectorPipe.transform(tenant.marketSegment.name)
        : tenant?.tenantName == null &&
          this.validateTenantOption(
            this.langSelectorPipe.transform(tenant?.tenantNameOption?.name)
          )
        ? "N/A"
        : "";
    }
  }
}
